import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DollarSign, Info } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MINIMUM_PAYOUT = 1000; // ₹1000 minimum payout threshold

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableCommission, setAvailableCommission] = useState(0);
  const [formData, setFormData] = useState({
    amount: '',
    bank_name: '',
    account_number: '',
    account_holder: '',
    ifsc_code: ''
  });

  useEffect(() => {
    fetchPayouts();
    fetchAvailableCommission();
  }, []);

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${API}/payouts`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch payouts');
      
      const data = await response.json();
      setPayouts(data);
    } catch (error) {
      toast.error('Failed to load payouts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCommission = async () => {
    try {
      const response = await fetch(`${API}/dashboard/stats`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      setAvailableCommission(data.pending_commission || 0);
    } catch (error) {
      console.error(error);
    }
  };

  // IFSC Code validation - RBI format: 4 letters (bank) + 0 + 6 alphanumeric (branch)
  const validateIFSC = (ifsc) => {
    if (!ifsc) return false; // Required for payout
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc.toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate IFSC code
    if (!validateIFSC(formData.ifsc_code)) {
      toast.error('Invalid IFSC Code format. Format: 4 letters + 0 + 6 alphanumeric (e.g., SBIN0001234)');
      return;
    }
    
    try {
      const response = await fetch(`${API}/payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount)
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create payout request');
      }
      
      toast.success('Payout request submitted successfully!');
      setDialogOpen(false);
      setFormData({
        amount: '',
        bank_name: '',
        account_number: '',
        account_holder: '',
        ifsc_code: ''
      });
      fetchPayouts();
      fetchAvailableCommission();
    } catch (error) {
      toast.error(error.message || 'Failed to submit payout request');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'processing':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-orange-100 text-orange-700 border-orange-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Payouts
          </h1>
          <p className="text-muted-foreground">Request commission payouts to your bank account</p>
        </div>
      </div>

      {/* Important Notes Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-blue-900 mb-2">Please Note:</p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Payouts are processed on the <strong>15th</strong> and <strong>30th</strong> of every month</li>
                <li>Minimum threshold for payout request is <strong>₹1,000</strong></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Commission Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle>Available for Payout</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-4xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ₹{availableCommission.toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                From paid referrals
              </p>
              {availableCommission < MINIMUM_PAYOUT && (
                <p className="text-sm text-orange-600 mt-1">
                  Minimum ₹{MINIMUM_PAYOUT.toLocaleString()} required to request payout
                </p>
              )}
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="request-payout-button" 
                  size="lg" 
                  disabled={availableCommission < MINIMUM_PAYOUT}
                  title={availableCommission < MINIMUM_PAYOUT ? `Minimum ₹${MINIMUM_PAYOUT.toLocaleString()} required` : ''}
                >
                  <DollarSign className="h-5 w-5 mr-2" />
                  Request Payout
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Payout</DialogTitle>
                  <DialogDescription>
                    Enter payout amount and bank details. Minimum payout: ₹{MINIMUM_PAYOUT.toLocaleString()}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="amount">Amount ₹ *</Label>
                    <Input
                      id="amount"
                      data-testid="payout-amount-input"
                      type="number"
                      step="0.01"
                      min={MINIMUM_PAYOUT}
                      max={availableCommission}
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Available: ₹{availableCommission.toFixed(2)} | Min: ₹{MINIMUM_PAYOUT.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="bank_name">Bank Name *</Label>
                    <Input
                      id="bank_name"
                      data-testid="bank-name-input"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="account_number">Account Number *</Label>
                    <Input
                      id="account_number"
                      data-testid="account-number-input"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="account_holder">Account Holder Name *</Label>
                    <Input
                      id="account_holder"
                      data-testid="account-holder-input"
                      value={formData.account_holder}
                      onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="ifsc_code">IFSC Code *</Label>
                    <Input
                      id="ifsc_code"
                      data-testid="ifsc-code-input"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })}
                      placeholder="e.g., SBIN0001234"
                      maxLength={11}
                      pattern="[A-Z]{4}0[A-Z0-9]{6}"
                      title="IFSC format: 4 letters + 0 + 6 alphanumeric"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: 4 letters + 0 + 6 alphanumeric</p>
                  </div>
                  <Button data-testid="submit-payout-button" type="submit" className="w-full">Submit Request</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Payout History */}
      <div>
        <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Payout History</h2>
        
        {payouts.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <DollarSign className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No payout requests yet</h3>
                <p className="text-muted-foreground mb-6">Request a payout when you have converted referrals</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {payouts.map((payout, index) => (
              <motion.div
                key={payout.payout_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card data-testid={`payout-card-${payout.payout_id}`} className="hover:shadow-md transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-4">
                          <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            ₹{payout.amount.toFixed(2)}
                          </p>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(payout.status)}`}>
                            {payout.status}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><span className="font-medium">Bank:</span> {payout.bank_name}</p>
                          <p><span className="font-medium">Account:</span> ****{payout.account_number.slice(-4)}</p>
                          <p><span className="font-medium">Holder:</span> {payout.account_holder}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <p>{new Date(payout.created_at).toLocaleDateString()}</p>
                        <p>{new Date(payout.created_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}